import React, { useState } from 'react';
import { Box, Typography, IconButton, Avatar, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DashboardIcon from '@material-ui/icons/Dashboard';
import BusinessIcon from '@material-ui/icons/Business';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import ExtensionIcon from '@material-ui/icons/Extension';
import PeopleIcon from '@material-ui/icons/People';
import DirectionsIcon from '@material-ui/icons/Directions';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import MenuIcon from '@material-ui/icons/Menu';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import GroupWorkIcon from '@material-ui/icons/GroupWork';
import TuneIcon from '@material-ui/icons/Tune';
import RoomIcon from '@material-ui/icons/Room';
import StarIcon from '@material-ui/icons/Star';
import AdminDashboard from '../pages/AdminDashboard';
import AdminLocations from '../pages/AdminLocations';
import AdminClients from '../pages/AdminClients';
import AdminGroups from '../pages/AdminGroups';
import AdminModules from '../pages/AdminModules';
import AdminProfiles from '../pages/AdminProfiles';
import AdminZones from '../pages/AdminZones';
import AdminUsers from '../pages/AdminUsers';
import AdminRoutes from '../pages/AdminRoutes';
import AdminVehicles from '../pages/AdminVehicles';
import AdminModel from '../pages/AdminModel';
import { COLORS } from '../../theme';

const SIDEBAR_WIDTH = 250;
const SIDEBAR_COLLAPSED = 68;

const NAV_ITEMS = [
  { key: 'dashboard', label: 'Dashboard', icon: DashboardIcon },
  { key: 'clients', label: 'Entidades / Organizações', icon: BusinessIcon },
  { key: 'groups', label: 'Grupos', icon: GroupWorkIcon },
  { key: 'profiles', label: 'Perfis', icon: TuneIcon },
  { key: 'zones', label: 'Zonas', icon: RoomIcon },
  { key: 'locations', label: 'Favoritos & POIs', icon: StarIcon },
  { key: 'users', label: 'Utilizadores', icon: PeopleIcon },
  { key: 'vehicles', label: 'Veículos', icon: LocalShippingIcon },
  { key: 'modules', label: 'Modulos', icon: ExtensionIcon },
  { key: 'routes', label: 'Rotas', icon: DirectionsIcon },
];

// Cor do chip de ícone de cada entrada (feel arejado, pastel por módulo).
const NAV_COLORS = {
  dashboard: '#0796cc', clients: '#6366f1', groups: '#0d9488', profiles: '#7c3aed',
  zones: '#e11d48', locations: '#d97706', users: '#2563eb', vehicles: '#0891b2',
  modules: '#10a774', routes: '#0ea5e9',
};

const useClassicStyles = makeStyles(() => ({
  '@global': {
    '@keyframes fadeIn': {
      '0%': { opacity: 0 },
      '100%': { opacity: 1 },
    },
    // Tema FuelCheck da MOLDURA (opt-in via toggle → data-admin-theme). As
    // regras abaixo usam var(--adm-*, <clássico>), logo sem este atributo
    // mantêm o verde EcooGPS; com ele, trocam para a identidade FuelCheck.
    '[data-admin-theme="fuelcheck"]': {
      '--adm-bg': '#e9eff5',
      '--adm-border': 'rgba(22,58,96,0.12)',
      '--adm-brand': '#0677a6',
      '--adm-brand-dark': '#055a80',
      '--adm-brand-mid': '#3d7fa3',
      '--adm-on-brand': '#ffffff',
      '--adm-brand-soft': 'rgba(6,119,166,0.08)',
      '--adm-footer-bg': '#f5f8fb',
      '--adm-glow': 'radial-gradient(ellipse at top left, rgba(7,150,204,0.06) 0%, transparent 70%)',
      '--adm-active-shadow': '0 4px 18px rgba(7,150,204,0.18)',
      '--adm-logo-shadow': '0 4px 12px rgba(7,150,204,0.28)',
      // Superfícies tingidas das páginas (secções/cabeçalhos) — passam de verde a azul.
      '--adm-section-bg': '#eef5fb',
      '--adm-section-border': '#d8e7f3',
      // Cabeçalhos de página LIMPOS (sem caixa tingida) — feel arejado.
      '--adm-header-bg': 'transparent',
      '--adm-header-border': 'transparent',
      // Tokens de superfície/texto para o ROLLOUT dos ecrãs (claro).
      '--adm-card': '#ffffff',
      '--adm-card-border': 'rgba(22,58,96,0.08)',
      '--adm-card-hover': '#f7fafd',
      '--adm-text': '#0b1a2c',
      '--adm-text-dim': '#5e7189',
      '--adm-text-muted': '#9fb0c0',
      '--adm-input-bg': '#ffffff',
      '--adm-input-border': 'rgba(22,58,96,0.14)',
      '--adm-hover': 'rgba(6,119,166,0.05)',
      '--adm-thead-bg': '#f2f6fb',
      '--adm-danger': '#e02a4f',
    },
    // Modo ESCURO — mesmos tokens, paleta navy/ciano. Os ecrãs que já consomem
    // var(--adm-*) flipam para escuro; o rollout por ecrã tokeniza o resto.
    '[data-admin-theme="fuelcheck-dark"]': {
      '--adm-bg': '#050b14',
      '--adm-border': 'rgba(146,175,205,0.16)',
      '--adm-brand': '#20a6d4',
      '--adm-brand-dark': '#1485ad',
      '--adm-brand-mid': '#5fc2e0',
      '--adm-on-brand': '#06121f',
      '--adm-brand-soft': 'rgba(32,166,212,0.14)',
      '--adm-footer-bg': '#0f2033',
      '--adm-glow': 'none',
      '--adm-active-shadow': '0 4px 18px rgba(32,166,212,0.28)',
      '--adm-logo-shadow': '0 4px 12px rgba(32,166,212,0.34)',
      '--adm-section-bg': '#14273d',
      '--adm-section-border': 'rgba(146,175,205,0.16)',
      '--adm-header-bg': 'transparent',
      '--adm-header-border': 'transparent',
      // Tokens de superfície/texto para o ROLLOUT dos ecrãs (escuro).
      '--adm-card': '#14273d',
      '--adm-card-border': 'rgba(146,175,205,0.14)',
      '--adm-card-hover': '#193150',
      '--adm-text': '#e7eef6',
      '--adm-text-dim': '#93a7be',
      '--adm-text-muted': '#6b7f96',
      '--adm-input-bg': '#0f2033',
      '--adm-input-border': 'rgba(146,175,205,0.20)',
      '--adm-hover': 'rgba(32,166,212,0.10)',
      '--adm-thead-bg': '#0f2033',
      '--adm-danger': '#e85d7a',
    },
  },
  root: {
    display: 'flex',
    height: '100vh',
    background: 'var(--adm-bg, #faf9f7)',
    overflow: 'hidden',
  },
  sidebar: {
    height: '100vh',
    background: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    flexShrink: 0,
    overflow: 'hidden',
    position: 'relative',
    borderRight: '1px solid var(--adm-border, #f1ece4)',
    boxShadow: '2px 0 18px rgba(52,71,103,0.05)',
    zIndex: 10,
  },
  sidebarGlow: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 200,
    background: 'var(--adm-glow, radial-gradient(ellipse at top left, rgba(249,115,22,0.05) 0%, transparent 70%))',
    pointerEvents: 'none',
  },
  sidebarHeader: {
    padding: '24px 16px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    borderBottom: '1px solid var(--adm-border, #f4f0ea)',
    minHeight: 76,
    position: 'relative',
  },
  logo: {
    width: 38,
    height: 38,
    background: 'var(--adm-brand, #2f5d50)',
    borderRadius: 11,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: 'var(--adm-logo-shadow, 0 4px 12px rgba(47,93,80,0.28))',
  },
  brandText: {
    display: 'flex',
    flexDirection: 'column',
  },
  brandName: {
    color: '#344767',
    fontWeight: 800,
    fontSize: '1.02rem',
    whiteSpace: 'nowrap',
    letterSpacing: '-0.01em',
  },
  brandSub: {
    color: '#9aa4b2',
    fontSize: '0.6rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginTop: 1,
  },
  navSection: {
    padding: '16px 10px 8px',
  },
  navSectionLabel: {
    fontSize: '0.58rem',
    fontWeight: 700,
    color: '#a0aec0',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    padding: '0 10px 8px',
  },
  navList: {
    flex: 1,
    padding: '8px 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '11px 14px',
    borderRadius: 10,
    cursor: 'pointer',
    color: '#5a6a7e',
    transition: 'all 0.18s ease',
    position: 'relative',
    '&:hover': {
      background: 'var(--adm-brand-soft, #eef5f1)',
      color: 'var(--adm-brand, #2f5d50)',
    },
  },
  navItemActive: {
    // Estado ativo — pill da cor de marca + texto/ícone de contraste.
    background: 'var(--adm-brand, #2f5d50)',
    color: 'var(--adm-on-brand, #c3f53c)',
    fontWeight: 700,
    boxShadow: 'var(--adm-active-shadow, 0 6px 16px rgba(47,93,80,0.3))',
    '&:hover': {
      background: 'var(--adm-brand-dark, #274c41)',
      color: 'var(--adm-on-brand, #c3f53c)',
    },
  },
  navLabel: {
    fontSize: '0.84rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  navIcon: {
    fontSize: 20,
    flexShrink: 0,
    transition: 'transform 0.2s',
  },
  sidebarFooter: {
    padding: '16px 14px',
    borderTop: '1px solid var(--adm-border, #f4f0ea)',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: 'var(--adm-footer-bg, #fdfbf8)',
  },
  avatar: {
    width: 34,
    height: 34,
    background: 'var(--adm-brand, #2f5d50)',
    fontSize: '0.72rem',
    fontWeight: 700,
    boxShadow: '0 2px 8px rgba(47,93,80,0.28)',
  },
  userInfo: {
    flex: 1,
    overflow: 'hidden',
  },
  userName: {
    fontSize: '0.78rem',
    fontWeight: 700,
    color: '#344767',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  userRole: {
    fontSize: '0.58rem',
    color: 'var(--adm-brand, #2f5d50)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontWeight: 700,
  },
  logoutBtn: {
    color: '#a99',
    transition: 'all 0.2s',
    '&:hover': { color: '#ef4444', background: 'rgba(239,68,68,0.1)' },
  },
  mainArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'var(--adm-bg, #faf9f7)',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 28px',
    background: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(12px)',
    borderBottom: '1px solid var(--adm-border, #f1ece4)',
  },
  topBarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  breadcrumb: {
    fontSize: '0.78rem',
    color: '#94a3b8',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  breadcrumbActive: {
    color: '#334155',
    fontWeight: 600,
  },
  toggleBtn: {
    color: '#94a3b8',
    transition: 'all 0.2s',
    '&:hover': { color: 'var(--adm-brand, #2f5d50)', background: 'var(--adm-brand-soft, rgba(47,93,80,0.07))' },
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#22c55e',
    boxShadow: '0 0 8px rgba(34,197,94,0.5)',
  },
  topBarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: 28,
    animation: '$fadeIn 0.3s ease',
  },
}));

// ─── Folha FuelCheck (opt-in via toggle) — CLARO e ESCURO ──────────────────
// REDESIGN (não recolor): superfícies planas, muito espaço, tipografia Inter,
// acento ciano, cantos suaves, sombras ténues. A MESMA fábrica gera as duas
// paletas (claro/escuro). Os nomes das classes espelham a folha clássica.
const FC_FONT = '"Inter","Inter Variable",-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif';
const FC_GRAD = 'linear-gradient(90deg, #58BAE6 0%, #18BDEE 45%, #2FD596 100%)';
const FC_GRAD_DK = 'linear-gradient(90deg, #6EC1E4 0%, #20C6F4 45%, #3DDC97 100%)';
const FC_LIGHT = {
  font: FC_FONT, bg: '#e9eff5', sidebar: '#ffffff', ink: '#0b1a2c', tx3: '#5e7189',
  accent: '#0677a6', cyan: '#0796cc', onAccent: '#ffffff', activeHover: '#055a80',
  line: 'rgba(22,58,96,0.10)', ok: '#0fa06c', brandSub: '#9fb0c0', navSectionCol: '#aab6c4',
  navHover: 'rgba(6,119,166,0.07)', activeShadow: '0 8px 18px rgba(6,119,166,0.22)',
  logoShadow: '0 6px 16px rgba(6,119,166,0.26)', topbar: 'rgba(245,248,251,0.82)',
  toggleHover: 'rgba(6,119,166,0.08)', logoutCol: '#e02a4f', logoutHover: 'rgba(224,42,79,0.09)',
  // Sidebar CLARA e arejada (feel premium leve): chips de ícone coloridos por
  // módulo, item ativo = "pílula" branca elevada. Marca aparece no logo/acentos.
  sbBg: '#f7f9fc', sbInk: '#0b1a2c', sbTx3: '#5e7189',
  sbHover: 'rgba(22,58,96,0.045)', sbHoverTx: '#0b1a2c', sbSectionCol: '#9fb0c0',
  sbBorder: 'rgba(22,58,96,0.07)', sbActiveBg: '#ffffff', sbActiveTx: '#0b1a2c',
  sbActiveShadow: '0 6px 18px rgba(20,42,70,0.10), inset 0 0 0 1px rgba(22,58,96,0.06)',
  sbAccentTx: '#0677a6', sbLogo: FC_GRAD,
  aura1: 'rgba(7,150,204,0.045)', aura2: 'rgba(16,167,116,0.04)',
};
const FC_DARK = {
  font: FC_FONT, bg: '#050b14', sidebar: '#0f2033', ink: '#e7eef6', tx3: '#93a7be',
  accent: '#20a6d4', cyan: '#2bb8d6', onAccent: '#06121f', activeHover: '#1485ad',
  line: 'rgba(146,175,205,0.16)', ok: '#22c08a', brandSub: '#6b7f96', navSectionCol: '#6b7f96',
  navHover: 'rgba(32,166,212,0.12)', activeShadow: '0 8px 18px rgba(32,166,212,0.28)',
  logoShadow: '0 6px 16px rgba(32,166,212,0.30)', topbar: 'rgba(11,23,38,0.82)',
  toggleHover: 'rgba(32,166,212,0.14)', logoutCol: '#e85d7a', logoutHover: 'rgba(232,93,122,0.14)',
  sbBg: '#0b1726', sbInk: '#e7eef6', sbTx3: '#93a7be',
  sbHover: 'rgba(146,175,205,0.07)', sbHoverTx: '#e7eef6', sbSectionCol: '#6b7f96',
  sbBorder: 'rgba(146,175,205,0.12)', sbActiveBg: '#152a44', sbActiveTx: '#e7eef6',
  sbActiveShadow: '0 6px 18px rgba(0,0,0,0.3), inset 0 0 0 1px rgba(32,198,244,0.22)',
  sbAccentTx: '#6EC1E4', sbLogo: FC_GRAD_DK,
  aura1: 'rgba(32,198,244,0.08)', aura2: 'rgba(61,220,151,0.06)',
};
const fuelShell = (P) => ({
  root: { display: 'flex', height: '100vh', background: P.bg, overflow: 'hidden', fontFamily: P.font },
  sidebar: {
    height: '100vh', background: P.sbBg, display: 'flex', flexDirection: 'column',
    transition: 'width 0.26s cubic-bezier(0.4,0,0.2,1)', flexShrink: 0, overflow: 'hidden',
    position: 'relative', borderRight: `1px solid ${P.sbBorder}`, zIndex: 10,
  },
  sidebarGlow: { display: 'none' },
  sidebarHeader: { padding: '26px 18px 22px', display: 'flex', alignItems: 'center', gap: 13, minHeight: 78, position: 'relative' },
  logo: {
    width: 40, height: 40, background: P.sbLogo,
    borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    boxShadow: P.sbActiveShadow,
  },
  brandText: { display: 'flex', flexDirection: 'column' },
  brandName: { color: P.sbInk, fontWeight: 800, fontSize: '1.08rem', whiteSpace: 'nowrap', letterSpacing: '-0.02em', fontFamily: P.font },
  brandSub: { color: P.sbAccentTx, fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', marginTop: 2, fontFamily: P.font },
  navSection: { padding: '20px 14px 6px' },
  navSectionLabel: { fontSize: '0.56rem', fontWeight: 700, color: P.sbSectionCol, textTransform: 'uppercase', letterSpacing: '0.16em', padding: '0 12px 6px', fontFamily: P.font },
  navList: { flex: 1, padding: '4px 12px', display: 'flex', flexDirection: 'column', gap: 3 },
  navItem: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderRadius: 14,
    cursor: 'pointer', color: P.sbInk, transition: 'all 0.16s var(--adm-ease, cubic-bezier(0.16,1,0.3,1))', position: 'relative', fontFamily: P.font,
    '&:hover': { background: P.sbHover },
  },
  navItemActive: {
    background: P.sbActiveBg, color: P.sbActiveTx, boxShadow: P.sbActiveShadow,
    '&:hover': { background: P.sbActiveBg },
  },
  navIconChip: {
    width: 34, height: 34, borderRadius: 10, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.16s',
  },
  navLabel: { fontSize: '0.88rem', fontWeight: 600, letterSpacing: '-0.01em', fontFamily: P.font, lineHeight: 1.18, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', wordBreak: 'break-word' },
  navIcon: { fontSize: 19, flexShrink: 0, transition: 'transform 0.2s' },
  sidebarFooter: { padding: '16px 16px', borderTop: `1px solid ${P.sbBorder}`, display: 'flex', alignItems: 'center', gap: 11, background: 'transparent' },
  avatar: { width: 36, height: 36, background: P.sbLogo, color: P.sbActiveTx, fontSize: '0.74rem', fontWeight: 700, fontFamily: P.font },
  userInfo: { flex: 1, overflow: 'hidden' },
  userName: { fontSize: '0.8rem', fontWeight: 700, color: P.sbInk, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: P.font },
  userRole: { fontSize: '0.56rem', color: P.sbAccentTx, textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 800, fontFamily: P.font },
  logoutBtn: { color: P.sbTx3, transition: 'all 0.2s', '&:hover': { color: '#ff8da3', background: 'rgba(255,77,109,0.14)' } },
  mainArea: {
    flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: P.bg, position: 'relative',
    // Aura da marca (assinatura FuelCheck): brilhos ciano/menta muito ténues no fundo.
    '&::before': { content: '""', position: 'absolute', top: '-10%', left: '-5%', width: '45vw', height: '45vw', borderRadius: '50%', background: `radial-gradient(circle, ${P.aura1}, transparent 68%)`, pointerEvents: 'none', zIndex: 0 },
    '&::after': { content: '""', position: 'absolute', bottom: '-14%', right: '-6%', width: '48vw', height: '48vw', borderRadius: '50%', background: `radial-gradient(circle, ${P.aura2}, transparent 68%)`, pointerEvents: 'none', zIndex: 0 },
  },
  topBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 32px',
    background: P.topbar, backdropFilter: 'blur(12px)', borderBottom: `1px solid ${P.line}`,
    position: 'relative', zIndex: 1,
  },
  topBarLeft: { display: 'flex', alignItems: 'center', gap: 16 },
  breadcrumb: { fontSize: '0.78rem', color: P.tx3, display: 'flex', alignItems: 'center', gap: 7, fontFamily: P.font },
  breadcrumbActive: { color: P.ink, fontWeight: 700 },
  toggleBtn: { color: P.tx3, transition: 'all 0.2s', '&:hover': { color: P.accent, background: P.toggleHover } },
  statusDot: { width: 8, height: 8, borderRadius: '50%', background: P.ok, boxShadow: `0 0 8px ${P.ok}80` },
  topBarRight: { display: 'flex', alignItems: 'center', gap: 12 },
  content: { flex: 1, overflow: 'auto', padding: '36px 40px', animation: '$fadeIn 0.3s ease', fontFamily: P.font, position: 'relative', zIndex: 1 },
});
const useFuelStyles = makeStyles(fuelShell(FC_LIGHT));
const useFuelDarkStyles = makeStyles(fuelShell(FC_DARK));

// Opções do seletor de visual (3 estados).
const THEME_OPTS = [
  { key: 'classic', label: 'Clássico', icon: '🎨' },
  { key: 'fuelcheck', label: 'Claro', icon: '☀️' },
  { key: 'fuelcheck-dark', label: 'Escuro', icon: '🌙' },
];

function AdminLayout({ user, token, onLogout, uiTheme, onPickTheme }) {
  const fc = uiTheme === 'fuelcheck' || uiTheme === 'fuelcheck-dark';
  const dark = uiTheme === 'fuelcheck-dark';
  // Todas as folhas são sempre criadas (hooks incondicionais); escolhe-se a ativa.
  const classicClasses = useClassicStyles();
  const fuelLightClasses = useFuelStyles();
  const fuelDarkClasses = useFuelDarkStyles();
  const classes = !fc ? classicClasses : (dark ? fuelDarkClasses : fuelLightClasses);
  const [page, setPage] = useState('dashboard');
  const [navParams, setNavParams] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  // Navegação entre ecrãs do backoffice, com parâmetros opcionais (usado pelos
  // links das colunas — ex.: Utilizadores → editar a Entidade/Organização).
  const navigate = (pg, params = null) => { setNavParams(params || null); setPage(pg); };

  function getInitials(name) {
    if (!name) return 'SA';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  }

  function getPageTitle(key) {
    const titles = { dashboard: 'Dashboard', model: 'Como funciona o modelo', clients: 'Entidades / Organizações', groups: 'Grupos', profiles: 'Perfis', zones: 'Zonas', locations: 'Favoritos & POIs', users: 'Utilizadores', vehicles: 'Veículos', modules: 'Modulos', routes: 'Rotas' };
    return titles[key] || 'Dashboard';
  }

  function renderPage() {
    switch (page) {
      case 'model':
        return <AdminModel onBack={() => setPage('dashboard')} />;
      case 'clients':
        return <AdminClients token={token} navParams={navParams} onNavigate={navigate} />;
      case 'groups':
        return <AdminGroups token={token} onNavigate={navigate} />;
      case 'profiles':
        return <AdminProfiles token={token} navParams={navParams} onNavigate={navigate} currentUser={user} />;
      case 'zones':
        return <AdminZones token={token} onNavigate={navigate} />;
      case 'locations':
        return <AdminLocations token={token} onNavigate={navigate} />;
      case 'users':
        return <AdminUsers token={token} navParams={navParams} onNavigate={navigate} currentUser={user} />;
      case 'vehicles':
        return <AdminVehicles token={token} onNavigate={navigate} />;
      case 'modules':
        return <AdminModules token={token} onNavigate={navigate} />;
      case 'routes':
        return <AdminRoutes token={token} onNavigate={navigate} />;
      default:
        return <AdminDashboard token={token} onNavigate={navigate} uiTheme={uiTheme} user={user} />;
    }
  }

  return (
    <Box className={classes.root} data-admin-theme={uiTheme || 'classic'}>
      <Box className={classes.sidebar} style={{ width: collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH }}>
        <Box className={classes.sidebarGlow} />

        <Box className={classes.sidebarHeader}>
          <Box className={classes.logo}>
            <DashboardIcon style={{ color: fc ? '#03121c' : '#fff', fontSize: 20 }} />
          </Box>
          {!collapsed && (
            <Box className={classes.brandText}>
              <Typography className={classes.brandName}>EcoPlanner</Typography>
              <Typography className={classes.brandSub}>Admin Panel</Typography>
            </Box>
          )}
        </Box>

        {!collapsed && (
          <Box className={classes.navSection}>
            <Typography className={classes.navSectionLabel}>Menu</Typography>
          </Box>
        )}

        <Box className={classes.navList}>
          {NAV_ITEMS.map(item => (
            <Tooltip key={item.key} title={collapsed ? item.label : ''} placement="right" arrow>
              <Box
                className={`${classes.navItem} ${page === item.key ? classes.navItemActive : ''}`}
                onClick={() => navigate(item.key)}
              >
                {fc ? (
                  <Box className={classes.navIconChip} style={{ background: `${NAV_COLORS[item.key] || '#64748b'}1f`, color: NAV_COLORS[item.key] || '#64748b' }}>
                    <item.icon style={{ fontSize: 19 }} />
                  </Box>
                ) : (
                  <item.icon className={classes.navIcon} />
                )}
                {!collapsed && <Typography className={classes.navLabel}>{item.label}</Typography>}
              </Box>
            </Tooltip>
          ))}
        </Box>

        <Box className={classes.sidebarFooter}>
          <Avatar className={classes.avatar}>{getInitials(user.name)}</Avatar>
          {!collapsed && (
            <>
              <Box className={classes.userInfo}>
                <Typography className={classes.userName}>{user.name}</Typography>
                <Typography className={classes.userRole}>Super Admin</Typography>
              </Box>
              <Tooltip title="Terminar sessao" arrow>
                <IconButton size="small" onClick={onLogout} className={classes.logoutBtn}>
                  <ExitToAppIcon style={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      <Box className={classes.mainArea}>
        {fc && <Box style={{ height: 3, flexShrink: 0, position: 'relative', zIndex: 1, background: 'var(--adm-brand-grad, linear-gradient(90deg, #58BAE6 0%, #18BDEE 45%, #2FD596 100%))' }} />}
        <Box className={classes.topBar}>
          <Box className={classes.topBarLeft}>
            <IconButton className={classes.toggleBtn} size="small" onClick={() => setCollapsed(c => !c)}>
              {collapsed ? <MenuIcon /> : <ChevronLeftIcon />}
            </IconButton>
            <Box className={classes.breadcrumb}>
              <span>Admin</span>
              <span style={{ color: '#cbd5e1' }}>/</span>
              <span className={classes.breadcrumbActive}>{getPageTitle(page)}</span>
            </Box>
          </Box>
          <Box className={classes.topBarRight}>
            {onPickTheme && (
              <Box style={{
                display: 'flex', alignItems: 'center', gap: 2, padding: 3, borderRadius: 999, marginRight: 6,
                border: `1px solid ${dark ? 'rgba(146,175,205,0.22)' : fc ? 'rgba(6,119,166,0.25)' : '#e2e8f0'}`,
                background: dark ? 'rgba(146,175,205,0.08)' : fc ? 'rgba(6,119,166,0.06)' : '#f8fafc',
              }}>
                {THEME_OPTS.map(opt => {
                  const on = (uiTheme || 'classic') === opt.key;
                  const activeBg = opt.key === 'fuelcheck-dark' ? '#20a6d4' : opt.key === 'fuelcheck' ? '#0677a6' : '#334155';
                  return (
                    <Tooltip key={opt.key} title={`Visual: ${opt.label}`} arrow>
                      <Box onClick={() => onPickTheme(opt.key)}
                        style={{
                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, padding: '4px 11px', borderRadius: 999,
                          fontSize: '0.71rem', fontWeight: 700, userSelect: 'none', transition: 'all 0.15s ease',
                          background: on ? activeBg : 'transparent',
                          color: on ? '#ffffff' : (dark ? '#93a7be' : '#64748b'),
                        }}>
                        <span>{opt.icon}</span>{opt.label}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            )}
            <Box className={classes.statusDot} />
            <Typography style={{ fontSize: '0.72rem', color: '#64748b' }}>
              {user.email}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.content}>
          {renderPage()}
        </Box>
      </Box>
    </Box>
  );
}

export default AdminLayout;
